import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  Spinner,
  Banner,
  Divider,
  Modal,
  Box,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { BillingHistoryTable, PaymentRecord, UsageProgressBar } from '../components/subscription';
import api from '../services/api';

interface SubscriptionInfo {
  id: number;
  status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'expired';
  billingCycle: 'monthly' | 'yearly';
  plan: {
    id: number;
    planKey: string;
    name: string;
    priceMonthly: number;
    priceYearly?: number;
    orderLimit?: number;
  };
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt?: string;
  cancelledAt?: string;
}

interface BillingState {
  subscription: SubscriptionInfo | null;
  usage: {
    ordersSynced: number;
    ordersLimit: number | null;
    billingPeriodStart: string;
    billingPeriodEnd: string;
  } | null;
  payments: PaymentRecord[];
  totalPayments: number;
  isLoading: boolean;
  error: string | null;
}

export const Billing: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<BillingState>({
    subscription: null,
    usage: null,
    payments: [],
    totalPayments: 0,
    isLoading: true,
    error: null,
  });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 10;

  const fetchData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const [subscriptionResponse, billingResponse] = await Promise.all([
        api.subscription.getCurrent(),
        api.subscription.getBillingHistory({ limit, offset: 0 }),
      ]);

      setState(prev => ({
        ...prev,
        subscription: subscriptionResponse.data.subscription,
        usage: subscriptionResponse.data.usage,
        payments: billingResponse.data.items,
        totalPayments: billingResponse.data.total,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load billing information. Please try again.',
      }));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadMorePayments = async () => {
    try {
      const newOffset = offset + limit;
      const response = await api.subscription.getBillingHistory({
        limit,
        offset: newOffset,
      });

      setState(prev => ({
        ...prev,
        payments: [...prev.payments, ...response.data.items],
      }));
      setOffset(newOffset);
    } catch (error) {
      console.error('Failed to load more payments:', error);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      await api.subscription.cancel(false);
      await fetchData();
      setCancelModalOpen(false);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to cancel subscription. Please try again.',
      }));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivate = async () => {
    try {
      await api.subscription.reactivate();
      await fetchData();
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to reactivate subscription. Please try again.',
      }));
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (pricePaise: number) => {
    return `₹${(pricePaise / 100).toFixed(0)}`;
  };

  const getStatusBadge = (status: string) => {
    const tones: Record<string, 'success' | 'warning' | 'critical' | 'info'> = {
      active: 'success',
      trialing: 'info',
      past_due: 'warning',
      cancelled: 'critical',
      expired: 'critical',
    };

    return <Badge tone={tones[status] || 'info'}>{status.replace('_', ' ').toUpperCase()}</Badge>;
  };

  if (state.isLoading) {
    return (
      <Page title="Billing">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" blockAlign="center">
                <Spinner size="large" />
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const { subscription, usage, payments, totalPayments } = state;

  return (
    <Page
      title="Billing & Subscription"
      subtitle="Manage your subscription and view billing history"
      backAction={{ content: 'Back', url: '/' }}
    >
      <Layout>
        {state.error && (
          <Layout.Section>
            <Banner
              title="Error"
              tone="critical"
              onDismiss={() => setState(prev => ({ ...prev, error: null }))}
            >
              <p>{state.error}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Current Subscription */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">Current Subscription</Text>
                {subscription && getStatusBadge(subscription.status)}
              </InlineStack>

              {subscription ? (
                <>
                  <BlockStack gap="300">
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodyMd" tone="subdued">Plan</Text>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {subscription.plan.name}
                      </Text>
                    </InlineStack>

                    <InlineStack align="space-between">
                      <Text as="span" variant="bodyMd" tone="subdued">Billing Cycle</Text>
                      <Text as="span" variant="bodyMd">
                        {subscription.billingCycle === 'yearly' ? 'Annual' : 'Monthly'}
                      </Text>
                    </InlineStack>

                    <InlineStack align="space-between">
                      <Text as="span" variant="bodyMd" tone="subdued">Price</Text>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {formatPrice(
                          subscription.billingCycle === 'yearly' && subscription.plan.priceYearly
                            ? subscription.plan.priceYearly
                            : subscription.plan.priceMonthly
                        )}
                        /{subscription.billingCycle === 'yearly' ? 'year' : 'month'}
                      </Text>
                    </InlineStack>

                    <InlineStack align="space-between">
                      <Text as="span" variant="bodyMd" tone="subdued">Order Limit</Text>
                      <Text as="span" variant="bodyMd">
                        {subscription.plan.orderLimit
                          ? `${subscription.plan.orderLimit.toLocaleString()} orders/month`
                          : 'Unlimited'}
                      </Text>
                    </InlineStack>

                    <Divider />

                    <InlineStack align="space-between">
                      <Text as="span" variant="bodyMd" tone="subdued">Current Period</Text>
                      <Text as="span" variant="bodyMd">
                        {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                      </Text>
                    </InlineStack>

                    {subscription.status === 'trialing' && subscription.trialEndsAt && (
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodyMd" tone="subdued">Trial Ends</Text>
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {formatDate(subscription.trialEndsAt)}
                        </Text>
                      </InlineStack>
                    )}

                    {subscription.cancelledAt && (
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodyMd" tone="subdued">Cancellation Date</Text>
                        <Text as="span" variant="bodyMd" tone="critical">
                          {formatDate(subscription.cancelledAt)}
                        </Text>
                      </InlineStack>
                    )}
                  </BlockStack>

                  <Divider />

                  <InlineStack gap="200" align="end">
                    <Button onClick={() => navigate('/pricing')}>
                      {subscription.status === 'trialing' ? 'Subscribe Now' : 'Change Plan'}
                    </Button>

                    {subscription.status === 'active' && !subscription.cancelledAt && (
                      <Button tone="critical" onClick={() => setCancelModalOpen(true)}>
                        Cancel Subscription
                      </Button>
                    )}

                    {subscription.cancelledAt && subscription.status !== 'expired' && (
                      <Button variant="primary" onClick={handleReactivate}>
                        Reactivate Subscription
                      </Button>
                    )}
                  </InlineStack>
                </>
              ) : (
                <BlockStack gap="300">
                  <Text as="span" variant="bodyMd" tone="subdued">
                    You don't have an active subscription.
                  </Text>
                  <Button variant="primary" onClick={() => navigate('/pricing')}>
                    View Plans
                  </Button>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Usage */}
        {usage && (
          <Layout.Section>
            <UsageProgressBar
              used={usage.ordersSynced}
              limit={usage.ordersLimit}
              periodStart={new Date(usage.billingPeriodStart)}
              periodEnd={new Date(usage.billingPeriodEnd)}
            />
          </Layout.Section>
        )}

        {/* Billing History */}
        <Layout.Section>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Billing History</Text>
            <BillingHistoryTable
              payments={payments}
              isLoading={state.isLoading}
              hasMore={payments.length < totalPayments}
              onLoadMore={loadMorePayments}
            />
          </BlockStack>
        </Layout.Section>

        {/* Cancel Modal */}
        <Modal
          open={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          title="Cancel Subscription"
          primaryAction={{
            content: 'Cancel Subscription',
            destructive: true,
            loading: isCancelling,
            onAction: handleCancelSubscription,
          }}
          secondaryActions={[
            {
              content: 'Keep Subscription',
              onAction: () => setCancelModalOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text as="span" variant="bodyMd">
                Are you sure you want to cancel your subscription?
              </Text>
              <Text as="span" variant="bodyMd" tone="subdued">
                You will continue to have access until the end of your current billing period
                ({subscription ? formatDate(subscription.currentPeriodEnd) : ''}).
                After that, your order sync will be paused.
              </Text>
              <Text as="span" variant="bodyMd" tone="subdued">
                You can reactivate your subscription at any time before the period ends.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
};

export default Billing;
